import { mean, stdev } from '../core/math';
import { notationOf, type ComboEvent, type PunchEvent } from './events';

/**
 * Combination assembler.
 *
 * A combination is not "N punches within T seconds" — that definition rewards
 * flailing. It is a run of punches whose *gaps* are short enough to be one
 * intent, thrown from a stable base. So we close a combo when either:
 *   - the gap exceeds `maxGapMs` (the player reset), or
 *   - the run reaches `maxLength` (anything longer is spam, not a combination).
 *
 * The event carries tempo statistics rather than a score, because "was this
 * combination good?" depends on what the opponent was doing, which the scorer
 * knows and the recogniser deliberately does not.
 */

const MAX_GAP_MS = 780;
const MAX_LENGTH = 8;

let nextId = 1;

export class ComboAssembler {
  private buf: PunchEvent[] = [];

  reset(): void {
    this.buf.length = 0;
  }

  get open(): readonly PunchEvent[] {
    return this.buf;
  }

  /** Feed a punch; returns a combo if this punch closed the previous run. */
  push(p: PunchEvent): ComboEvent | null {
    let closed: ComboEvent | null = null;
    const last = this.buf[this.buf.length - 1];
    if (last && p.tStart - last.tPeak > MAX_GAP_MS) {
      closed = this.close();
    }
    this.buf.push(p);
    if (this.buf.length >= MAX_LENGTH) {
      const forced = this.close();
      return closed ?? forced;
    }
    return closed;
  }

  /** Call every frame; closes a dangling run once the gap window elapses. */
  tick(now: number): ComboEvent | null {
    const last = this.buf[this.buf.length - 1];
    if (last && now - last.tPeak > MAX_GAP_MS) return this.close();
    return null;
  }

  private close(): ComboEvent | null {
    if (this.buf.length === 0) return null;
    const punches = this.buf;
    this.buf = [];
    // A single punch is a punch, not a combination. Emitting one-punch combos
    // would let a jab-only player farm "combination" scoring.
    if (punches.length < 2) return null;

    const gaps: number[] = [];
    for (let i = 1; i < punches.length; i++) gaps.push(punches[i].tStart - punches[i - 1].tPeak);

    const distinct = new Set(punches.map((p) => `${p.number}${p.target}`)).size;

    return {
      id: nextId++,
      punches,
      notation: notationOf(punches),
      tStart: punches[0].tStart,
      tEnd: punches[punches.length - 1].tPeak,
      tempoMs: mean(gaps),
      tempoJitterMs: stdev(gaps),
      variety: distinct / punches.length,
    };
  }
}

/**
 * Named combinations worth recognising, used for drills and for the AI's own
 * output. Kept small and canonical: a huge table of named combos is a content
 * trap that adds no mechanical depth.
 */
export const NAMED_COMBOS: ReadonlyArray<{ notation: string; name: string; tier: number }> = [
  { notation: '1-2', name: 'One-Two', tier: 1 },
  { notation: '1-1', name: 'Double Jab', tier: 1 },
  { notation: '1-2-3', name: 'Classic Three', tier: 2 },
  { notation: '1-2-1', name: 'Jab Sandwich', tier: 2 },
  { notation: '1-6-3', name: 'Up the Middle', tier: 3 },
  { notation: '2-3-2', name: 'Cross-Hook-Cross', tier: 3 },
  { notation: '1-2-5-2', name: 'Four-Piece', tier: 4 },
  { notation: '3-2-3', name: 'Mexican Special', tier: 4 },
  { notation: '1-2b-3', name: 'Body-Head Split', tier: 4 },
];

export function nameCombo(notation: string): { name: string; tier: number } | null {
  return NAMED_COMBOS.find((c) => c.notation === notation) ?? null;
}
